import { AppStorage } from "../model/AppStorage.js";
import { VOAttachment, VOBlog, VODay } from "../model/DataStructure.js";
import { Label } from "../model/Label.js";
import { Language, LanguageCode, Translation } from "../model/Language.js";
import { BlogDay } from "./Blog.js";

export interface BlogItemOptions {
    data: VOBlog;
    likesServiceUrl: string;
    day: BlogDay;
    index: number;
}

export class BlogItem {

    public readonly element: HTMLDivElement;
    public readonly data: VOBlog;
    public readonly day: BlogDay;

    private _index: number;

    constructor(options: BlogItemOptions) {
        this.element = document.createElement("div");
        this.element.className = "awi-blog__item";

        this.day = options.day;
        this._index = options.index;

        var content = document.createElement("div");
        content.className = "awi-blog__content";
        this.element.appendChild(content);

        var likesServiceUrl = options.likesServiceUrl;

        this.data = options.data;
        var data = options.data;

        if (data.media_de
            && data.media_de == data.media_en) {
            var image = this.createImage(data.media_de, null);
            image.classList.add("awi-blog__image-top");
            content.appendChild(image);
        } else {
            if (data.media_de) {
                var image = this.createImage(data.media_de, "de");
                image.classList.add("awi-blog__image-top");
                content.appendChild(image);
            }

            if (data.media_en) {
                var image = this.createImage(data.media_en, "en");
                image.classList.add("awi-blog__image-top");
                content.appendChild(image);
            }
        }

        var hasTitle = Boolean(data.title_de || data.title_en);
        var hasSubtitle = Boolean(data.subtitle_de || data.subtitle_en);
        var hasText = Boolean(data.text_de || data.text_en);

        var body = document.createElement("div");
        body.className = "awi-blog__body";
        content.appendChild(body);

        if (hasTitle || hasSubtitle) {
            var headline = document.createElement("div");
            headline.className = "awi-blog__headline";
            body.appendChild(headline);

            if (hasTitle) {
                var title = document.createElement("h2");
                title.className = "awi-blog__title";
                headline.appendChild(title);
                lang.set(title,
                    {
                        type: "text",
                        de: data.title_de,
                        en: data.title_en
                    });
            }

            if (hasSubtitle) {
                var subtitle = document.createElement("p");
                subtitle.className = "awi-blog__subtitle";
                headline.appendChild(subtitle);
                lang.set(subtitle,
                    {
                        type: "text",
                        de: data.subtitle_de,
                        en: data.subtitle_en
                    });
            }
        }

        if (data.media_de
            && data.media_de == data.media_en) {
            var image = this.createImage(data.media_de, null);
            image.classList.add("awi-blog__image-inline");
            body.appendChild(image);
        } else {
            if (data.media_de) {
                var image = this.createImage(data.media_de, "de");
                image.classList.add("awi-blog__image-inline");
                body.appendChild(image);
            }

            if (data.media_en) {
                var image = this.createImage(data.media_en, "en");
                image.classList.add("awi-blog__image-inline");
                body.appendChild(image);
            }
        }

        if (hasText) {
            var text = document.createElement("div");
            text.className = "awi-blog__text awi-rich-text";
            body.appendChild(text);
            lang.set(text,
                {
                    type: "html",
                    de: data.text_de,
                    en: data.text_en
                });
        }

        var footer = document.createElement("div");
        footer.className = "awi-blog__footer";
        body.appendChild(footer);

        var buttonsStart = document.createElement("div");
        buttonsStart.className = "awi-blog__buttons awi-blog__buttons--start";
        footer.appendChild(buttonsStart);

        var buttonsEnd = document.createElement("div");
        buttonsEnd.className = "awi-blog__buttons awi-blog__buttons--end";
        footer.appendChild(buttonsEnd);

        var tooltip: Translation = null;
        if (data.likes > 0) {
            tooltip = {
                de: data.likes + "",
                en: data.likes + ""
            };
        }
        var likeButton = this.addButton({
            icon: "fal fa-heart",
            ariaLabel: {
                de: "Gefällt mir",
                en: "I like it"
            },
            tooltip: tooltip
        });

        likeButton.disabled = AppStorage.getItem("liked-" + data.id) === "true";

        var like = likeButton.element;
        like.addEventListener("click", (e) => {
            if (likeButton.disabled) {
                return;
            }

            likeButton.disabled = true;
            likeButton.progress = true;
            var url = e5.Url.params.setEntries({
                blog: data.id + ""
            }, likesServiceUrl);
            jQuery.getJSON(url)
                .fail((f) => {
                    console.warn("Could not like blog", data.id, f.responseText);
                    likeButton.progress = false;
                    likeButton.disabled = false;
                })
                .done((response) => {
                    likeButton.progress = false;
                    AppStorage.setItem("liked-" + data.id, "true");
                    data.likes = parseInt(response.result);
                    var likeCount = response.result + "";
                    likeButton.setTooltip(
                        {
                            de: likeCount,
                            en: likeCount
                        }
                    );
                });
        });

        var shareButton = this.addButton({
            icon: "fal fa-share-alt",
            ariaLabel: {
                de: "Blogeintrag teilen",
                en: "Share blog"
            }
        });
        shareButton.element.setAttribute("aria-expanded", "false");
        shareButton.element.addEventListener("click", (e) => {
            window.share.share({
                initiator: shareButton.element,
                url: {
                    de: data.permalink_de,
                    en: data.permalink_en
                },
                info: {
                    de: `Teilen Sie diesen Blog Eintrag<br>"${e5.Text.escapeHtml(data.title_de)}"`,
                    en: `Share this blog entry<br>"${e5.Text.escapeHtml(data.title_en)}"`
                },
                description: {
                    de: data.text_de,
                    en: data.text_en
                },
                title: {
                    de: data.title_de,
                    en: data.text_en
                }
            });
        });

        if (data.edit_link) {
            this.addButton({
                icon: "fal fa-globe",
                url: {
                    de: data.permalink_de,
                    en: data.permalink_en
                },
                ariaLabel: {
                    de: "Webseite",
                    en: "website"
                }
            });

            this.addButton({
                icon: "fal fa-edit",
                url: data.edit_link,
                ariaLabel: {
                    de: "Bearbeiten",
                    en: "Edit"
                }
            });
        }
    }

    private createImage(media: VOAttachment, language: LanguageCode): HTMLDivElement {
        var image = document.createElement("div");
        image.className = "awi-blog__image";


        if (language) {
            image.classList.add("awi-base__only-" + language);
        }

        var container = document.createElement("div");
        container.className = "awi-blog__image-container";

        container.tabIndex = 0;
        container.setAttribute("role", "button");
        container.setAttribute("aria-expanded", "false");
        container.setAttribute("data-gallery", media.uid + "");
        Label.set(container, "main.open.gallery", "@aria-label");

        image.appendChild(container);

        var img = document.createElement("img");
        if (this._index > 2) {
            img.loading = "lazy";
        }
        img.src = media.sizes["medium_large"];
        img.width = media.sizes["medium_large-width"];
        img.height = media.sizes["medium_large-height"];
        img.draggable = false;
        lang.set(img,
            {
                type: "@title",
                de: media.info.title_de,
                en: media.info.title_en
            },
            {
                type: "@alt",
                de: media.info.description_de,
                en: media.info.description_en
            });
        container.appendChild(img);


        var icon = document.createElement("div");
        if (media.type === "video") {
            icon.className = "awi-blog__image-icon fal fa-play";
        } else {
            icon.className = "awi-blog__image-icon fal fa-search";
        }
        container.appendChild(icon);

        var caption = document.createElement("div");
        caption.className = "awi-blog__image-caption";
        lang.set(caption,
            {
                type: "html",
                de: media.info.author_de,
                en: media.info.author_en
            });
        image.appendChild(caption);

        return image;
    }

    private addButton(options: BlogButtonOptions): BlogButton {

        var btn = new BlogButton(options);

        var pos = options.position;
        if (!pos) {
            pos = "start";
        }

        this.element.querySelector(".awi-blog__buttons.awi-blog__buttons--" + pos).appendChild(btn.element);

        return btn;
    }

    public get offsetTop(): number {
        var offsetTop = this.element.offsetTop;
        var index = Array.from(this.element.parentNode.children).indexOf(this.element);
        if (index === 1) {
            offsetTop -= this.day.element.querySelector<HTMLElement>(".awi-blog__date").offsetHeight;
        }
        return offsetTop;
    }

    public get offsetHeight(): number {
        var offsetHeight = this.element.offsetHeight;
        var index = Array.from(this.element.parentNode.children).indexOf(this.element);
        if (index === 1) {
            offsetHeight += this.day.element.querySelector<HTMLElement>(".awi-blog__date").offsetHeight;
        }
        return offsetHeight;
    }
}

export interface BlogButtonOptions {
    tooltip?: Translation;
    ariaLabel: Translation;
    icon: string;
    url?: Translation;
    urlTarget?: string;
    position?: "start" | "end";
}

export class BlogButton {

    public readonly element: HTMLButtonElement | HTMLAnchorElement;

    constructor(options: BlogButtonOptions) {
        if (!options.url) {
            var btn = document.createElement("button");
            btn.type = "button";
            this.element = btn;
        } else {
            var a = document.createElement("a");
            lang.set(a, lang.type("@href", options.url))
            a.target = options.urlTarget || "_self";
            this.element = a;
        }

        this.element.className = "awi-blog__button";

        var icon = document.createElement("span");
        icon.className = "awi-blog__button-icon " + options.icon;
        this.element.appendChild(icon);

        if (options.ariaLabel) {
            lang.add(this.element, lang.type("@title", options.ariaLabel));
        }

        if (options.tooltip) {
            this.setTooltip(options.tooltip);
        }
    }

    public setTooltip(text: Translation): void {
        var lab = this.element.querySelector(".awi-blog__button-label");
        if (lab) {
            lab.remove();
        }

        var label = document.createElement("span");
        label.className = "awi-blog__button-label";
        label.setAttribute("aria-hidden", "true");
        this.element.appendChild(label);
        lang.set(label, text);
    }

    public get disabled(): boolean {
        return (this.element as any).disabled;
    }

    public set disabled(value: boolean) {
        (this.element as any).disabled = value;
    }

    public get progress(): boolean {
        return this.element.classList.contains("awi-blog__button--progress");
    }

    public set progress(value: boolean) {
        this.element.classList.toggle("awi-blog__button--progress", value);
    }
}